import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import Confetti from "react-confetti";
import { useMutation, useQuery } from "@apollo/react-hooks";

import {
  Container,
  Text,
  withStyles,
  Input,
  InputContainer,
  InfoIcon,
  PaymentDropdown,
  Drawer2,
  Button,
} from "components/common";

import {
  ACTIVATE_CARD,
  DASHBOARD_QUERY,
  UPDATE_PAYMENT_METHODS,
  REFETCH_PAYMENT_METHODS,
  UPDATE_PAYMENT_METHOD_INFO,
} from "utils/mutations";
import isKey from "utils/keyboard-events";
import GET_INVALID_INSTITUTE_IDS from "./gql-statements";
import { dateFormat, inputWidth, SUCCESS_STATUS } from "./consts";
import { formatExpirationMonth, isPaymentBlacklistedFinicityId } from "./utils";
import UpdateAccountDrawer from "./UpdateAccountDrawer";

import { track } from "../../../../utils/analytics";

const ActivateCard = ({ user, openModal, closeModal }) => {
  const { card } = user;
  const metaData = JSON.parse(card.metadata || "{}");
  const reissuedCard = metaData.reissued;

  const [paymentMethodId, setPaymentMethodId] = useState(
    user.default_payment_method_id || "",
  );

  const [needsPaymentMethodUpdate, setNeedsPaymentMethodUpdate] =
    useState(false);
  const [lastFour, setLastFour] = useState("");
  const [accountDrawerDescription, setAccountDrawerDescription] =
    useState(null);
  const [accountDrawerOnComplete, setAccountDrawerOnComplete] = useState(null);
  const [showBankInfoCompletedBanner, setShowBankInfoCompletedBanner] =
    useState(false);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [show, setShow] = useState(!card.activated || reissuedCard);
  const [loading, setLoading] = useState(false);
  const [routingNumber, setRoutingNumber] = useState(null);
  const [accountNumber, setAccountNumber] = useState(null);
  const [confirmAccountNumber, setConfirmAccountNumber] = useState(null);
  const [bankVerificationError, setBankVerificationError] = useState("");
  const [shouldShowBankVerification, setShouldShowBankVerification] =
    useState(false);
  const [accountUpdated, setAccountUpdated] = useState(false);

  const [activateCard] = useMutation(ACTIVATE_CARD);
  const [updatePaymentMethods] = useMutation(UPDATE_PAYMENT_METHODS);

  const lastFourRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const [updatePaymentMethodInfo] = useMutation(UPDATE_PAYMENT_METHOD_INFO);
  const { data } = useQuery(GET_INVALID_INSTITUTE_IDS);
  const invalidIds = data?.getSubstituteAccountNumberInstitutionIds;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paymentMethodsNeedUpdate, setPaymentMethodsNeedUpdate] = useState([]);
  const [accountUpdateSuccess, setAccountUpdateSuccess] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const setPaymentMethodParams = (data) => {
    setPaymentMethodsNeedUpdate(data);
    setSelectedPaymentMethodId(data[0].id);
    setSelectedPaymentMethod(data[0]);
  };

  useEffect(() => {
    if (paymentMethodId && invalidIds && !card.activated) {
      let paymentUpdateRequired = [];
      if (!accountUpdated) {
        paymentUpdateRequired = user.payment_methods.filter((account) => {
          const { manually_updated_on } = account;
          const isBlacklisted = isPaymentBlacklistedFinicityId(
            account,
            invalidIds,
          );
          return (
            (isBlacklisted && !manually_updated_on) ||
            account.needs_account_routing_numbers
          );
        });
        if (paymentUpdateRequired.length > 0) {
          setPaymentMethodParams(paymentUpdateRequired);
        }
      }
      setShouldShowBankVerification(paymentUpdateRequired.length > 0);
    }
  }, [invalidIds, paymentMethodId, accountUpdated]);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        setShow(false);
      }, 5000);
    }
  }, [success]);
  useEffect(() => {
    if (accountUpdateSuccess) {
      setTimeout(() => {
        setAccountUpdateSuccess(false);
      }, 5000);
    }
  }, [accountUpdateSuccess]);

  const getYear = () => `20${year}`;

  const validateExpiration = () => {
    const isValid = moment(`${month}-${getYear()}`, dateFormat).isValid();
    if (isValid) { setError(""); } else { setError("The expiration date is invalid"); }
    return isValid;
  };

  const validateLastFour = () => {
    const isValid = lastFour.length === 4;
    if (isValid) { setError(""); } else { setError("Please enter the last four digits on your card"); }
    return isValid;
  };

  const resetAccountUpdateErrorFields = () => {
    setError("");
    setBankVerificationError("");
    setLoading(true);
  };

  const cardActivation = async () => {
    if (shouldShowBankVerification) {
      setAccountDrawerDescription(
        "Please update your bank information before you activate your card",
      );
      setAccountDrawerOnComplete(() => {});
      setDrawerOpen(true);
      return;
    }
    if (!validateExpiration()) { return; }
    if (!validateLastFour()) { return; }
    resetAccountUpdateErrorFields();

    if (needsPaymentMethodUpdate) {
      const result = await updateDefaultPaymentMethod();
      if (result.success) {
        setError("");
        setNeedsPaymentMethodUpdate(false);
      } else {
        setError("Error updating payment method");
        setLoading(false);
        return;
      }
    }
    try {
      const { data: { activateCard: { status, message } = {} } = {} } =
        await activateCard({
          variables: {
            lastFour,
            month: formatExpirationMonth({ expirationMonth: month }),
            year: getYear(),
          },
          refetchQueries: [{ query: DASHBOARD_QUERY }],
        });
      if (status !== "OK") {
        setError(message);
      }
      if (status === "OK") {
        setSuccess(true);
      }
    } catch (err) {
      setError("There was an error processing your request");
    }
    setLoading(false);
  };

  const handlePaymentMethodChanged = (data = {}) => {
    setPaymentMethodId(data.id);
    setNeedsPaymentMethodUpdate(data.id !== user.default_payment_method_id);
  };

  const updateDefaultPaymentMethod = async () => {
    return updatePaymentMethods({
      variables: {
        user_id: user.email,
        default_payment_method_id: paymentMethodId,
      },
      refetchQueries: () => [{ query: REFETCH_PAYMENT_METHODS }],
      awaitRefetchQueries: true,
    })
      .then((res) => {
        track("DASHBOARD_ACTIVATE_CARD_PAYMENT_METHOD_CHANGE_SUCCESS");
        return { success: true };
      })
      .catch((e) => {
        track("DASHBOARD_ACTIVATE_CARD_PAYMENT_METHOD_CHANGE_FAILURE", {
          message: "Error updating payment methods",
        });
        return { success: false };
      });
  };

  const updateDefaultPaymentMethodInfo = async (selectedPaymentId = false) => {
    const args = {
      id: selectedPaymentId || paymentMethodId,
      accountNumber,
      routingNumber,
    };

    const res = await updatePaymentMethodInfo({
      variables: args,
      update: (cache, { data: response }) => {
        if (response?.updatePaymentMethodInfo?.status === SUCCESS_STATUS) {
          const data = cache.readQuery({
            query: DASHBOARD_QUERY,
          });

          cache.writeData({
            data: {
              user: {
                __typename: "User",
                payment_methods: data.user.payment_methods.map((pm) => {
                  if (pm.id === selectedPaymentId) {
                    return {
                      __typename: "UserPaymentMethod",
                      ...pm,
                      manually_updated_on: Date.now().toString(),
                      needs_account_routing_numbers: false,
                    };
                  }
                  return { __typename: "UserPaymentMethod", ...pm };
                }),
              },
            },
          });
        }
      },
    });
    const { status } = res.data.updatePaymentMethodInfo;
    if (status !== SUCCESS_STATUS) {
      setBankVerificationError(
        "Unable to validate banking information, please try again.",
      );
      return { success: false };
    }

    return {
      success: true,
    };
  };

  const renderActivateButton = () => (
    <button
      type="button"
      className="btn btn-green mt-3 px-10 py-2 w-full"
      onClick={cardActivation}
      disabled={loading}
    >
      Activate
    </button>
  );

  const renderSuccess = () => {
    return (
      <Container
        className="w-full flex-1 h-full lg:mt-0 bg-green-400 rounded"
        style={{ opacity: 0, transition: "opacity 5s ease-in" }}
      >
        <Confetti colors={["#26E3BA", "#011516", "#e2e8f0"]} />
        <div className="flex justify-between md:flex-col">
          <div className="flex flex-col m-auto items-center text-white font-bold py-4">
            Success!
          </div>
        </div>
      </Container>
    );
  };

  if (!show) {
    return null;
  }

  const onAddBankInfoCompleteWithoutActivation = () => {
    setShowBankInfoCompletedBanner(true);
    setTimeout(() => {
      setShowBankInfoCompletedBanner(false);
    }, 5000);
  };

  const onAddBankInfoClick = () => {
    setAccountDrawerDescription("");
    setAccountDrawerOnComplete(() => onAddBankInfoCompleteWithoutActivation);
    setDrawerOpen(true);
  };

  return success ? (
    renderSuccess()
  ) : (
    <>
      {showBankInfoCompletedBanner && (
        <Container
          className="flex-row flex-1 mb-4 lg:mt-0 bg-green-secondary text-black"
          heightFull={false}
        >
          <span className="flex flex-row items-baseline">
            <i className="fas fa-check text-gray-primary mr-2 font-semibold" />
            <p>
              Yay! All set for now. Sit tight and wait for your card to arrive.
            </p>
          </span>
        </Container>
      )}

      {shouldShowBankVerification && (
        <Container
          className="flex-row flex-1 mb-4 lg:mt-0 bg-beige"
          heightFull={false}
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <span className="flex flex-row items-baseline">
              <i className="fas fa-exclamation-triangle text-red-500 mr-2" />
              <p>
                Action needed: Update your account and routing number for your
                card to be shipped.
              </p>
            </span>
            <Button
              className="font-normal mt-2 sm:mt-0"
              onClick={onAddBankInfoClick}
            >
              Finish Setup
            </Button>
          </div>
        </Container>
      )}

      <Container className="w-full flex-1 pt-6 mt-4 lg:mt-0">
        <div className="flex justify-between md:flex-col md:items-center items-start">
          <div className="flex flex-col items-start">
            <div className="flex">
              <InfoIcon className="flex items-center mr-1" />
              <Text className="font-bold text-base ml-1 md:text-xl text-center text-black-primary font-title">
                Activate Your Card
              </Text>
            </div>

            <div className="flex flex-col mt-2 lg:justify-between lg:flex-row md:items-baseline">
              {reissuedCard ? (
                <Text className="text-base">
                  Your reissued card is on its way! Once you receive it activate
                  it here. In the meantime, continue to use your current card
                  until your new card arrives.
                </Text>
              ) : (
                <Text className="text-base">
                  Welcome to Tomo! You should receive your card within 7-10
                  business days. Once received, enter the information here to
                  activate.
                </Text>
              )}
              <div className="flex flex-col mt-6 lg:mt-0 lg:ml-6 sm:w-full">
                <div className="flex flex-col sm:flex-row">
                  <div className="w-full" style={{ minWidth: inputWidth }}>
                    <Text className="text-base whitespace-nowrap lg:absolute lg:-mt-6">
                      Last 4 digits on card
                    </Text>
                    <InputContainer>
                      <Input
                        placeholder="XXXX"
                        ref={lastFourRef}
                        type="number"
                        value={lastFour || ""}
                        onKeyDown={(e) => {
                          if (
                            !isKey(e, ["number", "backspace", "delete", "tab"])
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          e.target.value.length < 5 &&
                            setLastFour(e.target.value);
                          if (e.target.value.length === 4) {
                            monthRef.current.focus();
                          }
                        }}
                      />
                    </InputContainer>
                    {!shouldShowBankVerification && (
                      <div className="hidden lg:block">
                        {renderActivateButton()}
                      </div>
                    )}
                  </div>
                  <div
                    className="mt-4 sm:mt-0 sm:ml-4 w-full"
                    style={{ minWidth: inputWidth }}
                  >
                    <Text className="text-base lg:absolute lg:-mt-6">
                      Expiration Date
                    </Text>
                    <InputContainer>
                      <p
                        className={`absolute ml-8 ${
                          month.length === 2 ? "text-black" : "text-gray-500"
                        }`}
                      >
                        /
                      </p>
                      <Input
                        placeholder="MM"
                        type="number"
                        ref={monthRef}
                        value={month || ""}
                        className="w-12"
                        widthFull={false}
                        onKeyDown={(e) => {
                          if (
                            !isKey(e, ["number", "backspace", "delete", "tab"])
                          ) {
                            e.preventDefault();
                          }
                          if (isKey(e, ["backspace"]) && month.length === 0) {
                            e.preventDefault();
                            lastFourRef.current.focus();
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value.length < 3) {
                            setMonth(e.target.value);
                          }
                          if (e.target.value.length === 2) {
                            yearRef.current.focus();
                          }
                        }}
                      />
                      <Input
                        placeholder="YY"
                        type="number"
                        ref={yearRef}
                        value={year || ""}
                        onKeyDown={(e) => {
                          if (
                            !isKey(e, ["number", "backspace", "delete", "tab"])
                          ) {
                            e.preventDefault();
                          }
                          if (isKey(e, ["backspace"]) && year.length === 0) {
                            monthRef.current.focus();
                          }
                        }}
                        onChange={(e) => {
                          if (e.target.value.length < 3) {
                            setYear(e.target.value);
                          }
                          if (e.target.value.length > 1 && month.length === 2) {
                            validateExpiration();
                          }
                        }}
                      />
                    </InputContainer>
                  </div>
                </div>
                {error && (
                  <p className="hidden lg:block mt-2 text-red-600">{error}</p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-start w-full mt-4 lg:mt-0 lg:ml-6">
                <div className="w-full">
                  <Text className="text-base whitespace-nowrap lg:absolute lg:-mt-6">
                    Default Payment Method
                  </Text>
                  <div>
                    <PaymentDropdown
                      paymentMethods={user.payment_methods}
                      defaultElementId={paymentMethodId || null}
                      didSelect={handlePaymentMethodChanged}
                      listItemInnerClassName="py-2 truncate"
                      listItemImageClassName="inline-block h-6 mr-2"
                    />
                  </div>
                  {!shouldShowBankVerification && (
                    <div className="p-2 text-xs leading-none">
                      By clicking the 'Activate' button, I agree to enroll
                      Weekly Autopay. Please see ACH authorization{" "}
                      <span
                        onClick={(e) =>
                          openModal(e, "ACHAuthorizationDetailsModal", {
                            closeModal,
                          })
                        }
                        className="cursor-pointer italic font-bold"
                      >
                        here
                      </span>{" "}
                      for more details. You can opt out of Autopay by contacting
                      help@tomocredit.com.
                    </div>
                  )}
                </div>
                {!shouldShowBankVerification && (
                  <div className="lg:hidden sm:ml-4 flex w-full mt-3">
                    {renderActivateButton()}
                  </div>
                )}
              </div>
              {error && (
                <p className="lg:hidden mt-4 text-center text-red-600">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>

        {shouldShowBankVerification && (
          <>
            <div className="hidden w-3/12 mt-2 lg:block">
              {renderActivateButton()}
            </div>
            <div className="lg:hidden flex w-full mt-2">
              {renderActivateButton()}
            </div>
            <div className="p-2 w-full lg:w-4/12 mt-2 text-xs text-gray-500 leading-none ">
              By clicking the 'Activate' button, I acknowledge that if there is
              a problem with my default payment method, Tomo will automatically
              charge a backup payment method.
            </div>
          </>
        )}
      </Container>
      <Drawer2 isOpen={drawerOpen} close={() => setDrawerOpen(false)}>
        <UpdateAccountDrawer
          description={accountDrawerDescription}
          paymentMethodsNeedUpdate={paymentMethodsNeedUpdate}
          updateDefaultPaymentMethodInfo={updateDefaultPaymentMethodInfo}
          routingNumber={routingNumber}
          setRoutingNumber={setRoutingNumber}
          accountNumber={accountNumber}
          setAccountNumber={setAccountNumber}
          confirmAccountNumber={confirmAccountNumber}
          setConfirmAccountNumber={setConfirmAccountNumber}
          bankVerificationError={bankVerificationError}
          loading={loading}
          setLoading={setLoading}
          resetAccountUpdateErrorFields={resetAccountUpdateErrorFields}
          setPaymentMethodsNeedUpdate={setPaymentMethodsNeedUpdate}
          setDrawerOpen={setDrawerOpen}
          setBankVerificationError={setBankVerificationError}
          setAccountUpdated={setAccountUpdated}
          accountUpdateSuccess={accountUpdateSuccess}
          setAccountUpdateSuccess={setAccountUpdateSuccess}
          selectedPaymentMethodId={selectedPaymentMethodId}
          setSelectedPaymentMethodId={setSelectedPaymentMethodId}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          onDrawerActionsComplete={accountDrawerOnComplete}
        />
      </Drawer2>
    </>
  );
};

export default withStyles(ActivateCard);
